/**
 * @file BusinessImpact — data-driven business-impact assessment
 * All content is sourced from location.state.assessmentsData.sections
 */

import { useLayoutEffect, useEffect, useRef, useState, useMemo, type CSSProperties, type ReactNode, useContext } from 'react';
import { useLocation, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { AssessmentBackground } from '../../hd-ui-kit/src/components/ui/AssessmentBackground';
import { InfoRow } from '../../hd-ui-kit/src/components/ui/InfoRow';
import { AssessmentQuestionBackground } from '../../hd-ui-kit/src/components/ui/AssessmentQuestionBackground';
import { AssessmentNav } from '../../hd-ui-kit/src/components/ui/AssessmentNav';
import { Footer } from '../../hd-ui-kit/src/components/ui/Footer/Footer';
import type { FooterLink } from '../../hd-ui-kit/src/components/ui/Footer/Footer';
import { AssessmentStep } from '../../hd-ui-kit/src/components/screens/AssessmentStep';
import { AssessmentQuestion } from '../../hd-ui-kit/src/components/screens/AssessmentQuestion';
import { StepTransition } from '../../hd-ui-kit/src/components/ui/StepTransition';
import { Button, ButtonArrow, ButtonSpinner } from '../../hd-ui-kit/src/components/ui/Button';
import { AssessmentExample } from '../../hd-ui-kit/src/components/ui/AssessmentExample';
import { AssessmentSectionList } from '../../hd-ui-kit/src/components/ui/AssessmentSectionList';
import type { AssessmentExampleItem } from '../../hd-ui-kit/src/components/ui/AssessmentExample';
import type { AssessmentScaleItem } from '../../hd-ui-kit/src/components/ui/AssessmentScale';
import { heidrickTheme } from '../../hd-ui-kit/src/theme';
import { ThemeProvider } from '@mui/material/styles';
import '../../hd-ui-kit/src/styles/index.css';
import DOMPurify from 'dompurify';
import Api from '../../Api';
import Loader from '../../commons/Loader';
import { globalContext } from '../../store';
import { applyGlobalBranding as applyGlobalBrandingCommon, footerValues } from '../../commons/commonFunctions';

const BRAND = 'mayo' as const;

type QuestionData = { skill: string; description: string; section?: string };
// ── Small reusable helpers ────────────────────────────────────────────────

const SecondaryOnDark = (props: React.ComponentProps<typeof Button>) => (
  <Button
    {...props}
    variant="secondary"
    sx={{
      color: '#FFFFFF',
      border: '1px solid rgba(255, 255, 255, 0.85)',
      '&:hover, &:active, &[data-force-hover]': { border: '1.5px solid #FFFFFF' },
      ...(props.sx as object),
    }}
  />
);

function SaveProgressNote({ text }: { text: string }) {
  return (
    <p style={{
      margin: 0,
      fontFamily: 'var(--hd-text-hero-body-family)',
      fontSize: 'var(--hd-text-hero-body-size)',
      fontWeight: 'var(--hd-text-hero-body-weight)' as CSSProperties['fontWeight'],
      lineHeight: 'var(--hd-text-hero-body-line-height)',
      color: 'var(--hd-text-hero-body-color)',
    }}>
      {text}
    </p>
  );
}

function HowToRespondSections({
  isLight,
  sections,
}: {
  isLight?: boolean;
  sections: Array<{ heading: string; body: string }>;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {sections.map((s, i) => (
        <div key={s.heading} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {i > 0 && (
            <div
              aria-hidden
              style={{ width: 32, height: 1, background: isLight ? 'rgba(0,33,54,0.20)' : 'rgba(255,255,255,0.2)' }}
            />
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <h2 style={{
              margin: 0,
              fontFamily: 'var(--hd-text-assessment-section-family) !important',
              fontSize: 'var(--hd-text-assessment-section-size) !important',
              fontWeight: 'var(--hd-text-assessment-section-weight)' as CSSProperties['fontWeight'],
              lineHeight: 'var(--hd-text-assessment-section-line-height)',
              color: 'var(--hd-text-assessment-section-color)',
            }}>
              {s.heading}
            </h2>
            <div style={{
              fontFamily: 'var(--hd-font-family-avenir)',
              fontSize: 19,
              fontWeight: 350,
              lineHeight: 'normal',
              color: isLight ? '#334255' : '#A9A9A9',
            }}>
              {s.body}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function RatingScaleContent({
  isLight,
  ratingFactors,
  scalePrompt,
  scaleItems,
}: {
  isLight?: boolean;
  ratingFactors: Array<{ label: string; question: string }>;
  scalePrompt: string;
  scaleItems: readonly AssessmentExampleItem[];
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 40, width: '100%', marginTop: -24 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {ratingFactors.map((c, i) => (
          <div key={c.label} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {i > 0 && (
              <div
                aria-hidden
                style={{ width: 32, height: 1, background: isLight ? 'rgba(0,33,54,0.20)' : 'rgba(255,255,255,0.2)' }}
              />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontFamily: 'var(--hd-font-family-avenir)', fontSize: 22, fontWeight: 500, lineHeight: 'normal', color: isLight ? '#00566A' : '#66FCFC' }}>
                {c.label}
              </div>
              <div style={{ fontFamily: 'var(--hd-font-family-avenir)', fontSize: 19, fontWeight: 350, lineHeight: 'normal', color: isLight ? '#334255' : '#A9A9A9' }}>
                {c.question}
              </div>
            </div>
          </div>
        ))}
      </div>
      <p style={{ margin: 0, fontFamily: 'var(--hd-font-family-avenir)', fontSize: 22, fontWeight: 300, lineHeight: '28px', color: isLight ? '#334255' : '#CCCCCC' }}>
        {scalePrompt}
      </p>
      <AssessmentExample items={scaleItems} theme={isLight ? 'light' : 'dark'} />
    </div>
  );
}

function RatingExampleContent({
  isLight,
  exampleQ,
  scaleItems,
}: {
  isLight?: boolean;
  exampleQ: { title: string; description: string; preSelectedValue: number };
  scaleItems: readonly AssessmentExampleItem[];
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 40, width: '100%', marginTop: -24 }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, maxWidth: 800, alignSelf: 'center', width: '100%' }}>
        <div aria-hidden style={{ width: 32, height: 1, background: isLight ? 'rgba(0,33,54,0.20)' : 'rgba(255,255,255,0.2)' }} />
        <div style={{ fontFamily: 'var(--hd-font-family-avenir-lt-pro)', fontSize: 28, fontWeight: 350, lineHeight: '34px', color: isLight ? '#002136' : '#FFFFFF', textAlign: 'center' }}>
          {exampleQ.title}
        </div>
        <p style={{ margin: 0, fontFamily: 'var(--hd-font-family-avenir)', fontSize: 22, fontWeight: 350, lineHeight: 'normal', color: isLight ? '#334255' : '#CCCCCC', textAlign: 'center' }}>
          {exampleQ.description}
        </p>
      </div>
      <AssessmentExample items={scaleItems} example={exampleQ.preSelectedValue as 1 | 2 | 3 | 4 | 5} theme={isLight ? 'light' : 'dark'} />
    </div>
  );
}

function CheckIcon({ isLight }: { isLight?: boolean }) {
  const ring  = isLight ? 'rgba(0, 86, 106, 0.3)' : 'rgba(102, 252, 252, 0.3)';
  const check = isLight ? '#00566A' : '#66FCFC';
  return (
    <div style={{ width: 80, height: 80, borderRadius: '50%', border: `1px solid ${ring}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }} aria-hidden="true">
      <svg width="32" height="24" viewBox="0 0 32 24" fill="none">
        <path d="M2.5 12.5 L12 21.5 L29.5 2.5" stroke={check} strokeWidth="3.281" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function StepActions({
  isLight,
  onBack,
  label,
  onContinue,
  submitting,
}: {
  isLight?: boolean;
  onBack?: () => void;
  label: string;
  onContinue: () => void;
  submitting?: boolean;
}) {
  const Secondary = isLight
    ? (props: React.ComponentProps<typeof Button>) => <Button variant="secondary" {...props} />
    : SecondaryOnDark;
  return (
    <>
      {onBack && <Secondary size="md" onClick={onBack} disabled={submitting}>Back</Secondary>}
      <Button variant="primary" size="md" tone={isLight ? 'dark' : 'bright'} endIcon={submitting ? <ButtonSpinner /> : <ButtonArrow />} onClick={onContinue} disabled={submitting}>
        {label}
      </Button>
    </>
  );
}

// ── Section helpers ───────────────────────────────────────────────────────

const findSection = (sections: any[], type: string) => sections.find((s: any) => s.type === type);
const sectionPage  = (sections: any[], type: string, pageIdx = 0) =>
  findSection(sections, type)?.pages?.[pageIdx]?.content ?? {};

// Unescape sequences like \' that some API values include
const unescape = (s: string) => s?.replace(/\\'/g, "'") ?? '';

const buildAnswersPayload = (src: Record<string, { score: number; title: string }>) =>
  Object.entries(src).map(([sid, { score, title }]) => ({
    StatementID: sid, StatementTitle: title, Score: String(score), Answer: null, Comment: null,
  }));

// Validate URL to prevent javascript: protocol injection (DOM XSS mitigation)
const sanitizeImageUrl = (url: string | undefined | null): string => {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  // Allow only safe protocols: https, http, relative paths, or data URIs for images
  if (
    trimmed.startsWith('https://') ||
    trimmed.startsWith('http://') ||
    trimmed.startsWith('/') ||
    trimmed.startsWith('data:image/')
  ) {
    return trimmed;
  }
  return '';
};

// Sanitize text to prevent potential XSS in text contexts
const sanitizeText = (text: string | undefined | null, fallback = ''): string => {
  if (!text || typeof text !== 'string') return fallback;
  // Strip HTML tags and decode entities to get plain text
  const sanitized = DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
  return sanitized || fallback;
};

// Create a small stable hash from a string to use as a React key (avoids using array index)
const hashString = (s: string): string => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0; // convert to 32bit int
  }
  return 'k' + (h >>> 0).toString(36);
};

// ── Main component ────────────────────────────────────────────────────────

export default function BusinessImpact({ theme = 'light', footerLinks }: { theme?: 'light' | 'dark'; footerLinks?: FooterLink[] } = {}) {
  const { globalState, dispatch } = useContext(globalContext);
  const isLight  = theme === 'light';
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: assessmentStructureId } = useParams<{ id: string }>();
  const isPreviewMode = searchParams.get('preview') === 'true';
  const [previewAssessmentData, setPreviewAssessmentData] = useState<any>(null);
  const [previewFirstName, setPreviewFirstName] = useState<string>('');
  const assessmentData: any  = isPreviewMode ? (previewAssessmentData ?? {}) : (location.state?.assessmentsData ?? {});
  const sections: any[]      = assessmentData?.sections ?? [];
  const [headerLogo, setHeaderLogo] = useState<string>('');
  const [, setHeaderImage] = useState<string>('');
  const [, setPrimaryColor] = useState<string>('#BA3A19');

  useEffect(() => {
    applyGlobalBrandingCommon(globalState, setHeaderLogo, setHeaderImage, setPrimaryColor, dispatch);
    if (isPreviewMode) {
      Api.getUserDetails().then(res => {
        if (res?.success) {
          setPreviewFirstName(res.response?.data?.data?.firstName ?? '');
        }
      });
    }
    if (!location.state?.assessmentsData) {
      if (isPreviewMode && assessmentStructureId) {
        Api.getLeaderAssessmentStructureDetails(assessmentStructureId, 1).then(res => {
          if (res?.success) {
            const data = res.response?.data?.data?.[0] ?? res.response?.data?.[0] ?? res.response?.data;
            setPreviewAssessmentData(data);
          }
        });
        return;
      }
      navigate('/dashboard', { replace: true });
    }
  }, [location.state, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Parse section content ──────────────────────────────────────────────
  const globalSection = useMemo(() => sections?.find((s): s is any => s.type === 'Global'), [sections]);
  const computedFooterLinks = useMemo<FooterLink[] | undefined>(() => {
    if (!globalSection?.footer) return undefined;
    const footerData = globalSection.footer;
    return [
      { label: footerData.privacyPolicy?.text ?? 'Privacy Notice', href: footerData.privacyPolicy?.url, target: '_blank' },
      { label: footerData.legalPolicy?.text ?? 'Legal Notice', href: footerData.legalPolicy?.url, target: '_blank' },
      { label: footerData.needHelp?.text ?? 'Need Help?', href: footerData.needHelp?.url, target: '_blank' },
    ];
  }, [globalSection]);
  const introPage    = findSection(sections, 'Introduction')?.pages?.[0] ?? {};
  const introContent = introPage.content  ?? {};
  const introMeta    = introPage.metadata ?? {};

  const instrPages   = findSection(sections, 'Instructions')?.pages ?? [];
  const ratScalePage = instrPages[0]?.content ?? {};
  const ratExPage    = instrPages[1]?.content ?? {};
  const howToPage    = instrPages[2]?.content ?? {};

  const orgIntro    = sectionPage(sections, 'organizational-impact-introduction');
  const funcIntro   = sectionPage(sections, 'functional-impact-introduction');
  const submissionPg = sectionPage(sections, 'submission');
  const completionPg = sectionPage(sections, 'Completion');

  // Preserve original array order for sub-functions
  const subFuncIntroSections = sections.filter((s: any) => s.type === 'functional-impact-sub-function-introduction');
  const funcQuestionSections = sections.filter((s: any) => s.type === 'functional-impact-questions');
  const numSubFunctions      = subFuncIntroSections.length;

  // Gather statementIds by question group
  const orgStatementIds: string[] = (findSection(sections, 'organizational-impact-questions')?.pages ?? [])
    .flatMap((p: any) => p.statementIds ?? []);
  const subFuncStatementIds: string[][] = funcQuestionSections.map((s: any) =>
    (s.pages ?? []).flatMap((p: any) => p.statementIds ?? [])
  );

  // ── Derived display values ─────────────────────────────────────────────

  const assessmentName    = sanitizeText(assessmentData?.name, 'Business Impact Inventory');

  const leaderFirstName   = globalState?.leaderData?.firstName
    || assessmentData?.leaderDetails?.leaderFirstName
    || '';
  const completionHeading = isPreviewMode
    ? (completionPg.heading ?? 'You did it, {firstName}.').replace('{firstName}', previewFirstName).trim()
    : (completionPg.heading ?? 'You did it.').replace('{firstName}', leaderFirstName).trim();

  const infoRowStats = [
    introMeta.totalQuestionCount       && { number: String(introMeta.totalQuestionCount.value),           label: introMeta.totalQuestionCount.displayText       ?? 'Questions'      },
    introMeta.estimatedDurationMinutes && { number: String(introMeta.estimatedDurationMinutes.value),     label: introMeta.estimatedDurationMinutes.displayText ?? 'Estimated Time' },
  ].filter(Boolean) as Array<{ number: string; label: string }>;

  const ratingFactors = (ratScalePage.ratingFactors ?? []).map((f: any) => ({
    label: f.title, question: f.description,
  }));

  const howToSections = (howToPage.guidelines ?? []).map((g: any) => ({
    heading: g.title, body: g.description,
  }));

  const funcSubSectionLabels: string[] = (funcIntro.subSections ?? []).map((s: any) => s.label);

  const exampleQuestion = ratExPage.exampleQuestion as { title: string; description: string; preSelectedValue: number } | undefined;

  // ── Fetch question content from statementIds ───────────────────────────

  const [orgQuestions,     setOrgQuestions]     = useState<QuestionData[]>([]);
  const [subFuncQuestions, setSubFuncQuestions] = useState<QuestionData[][]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [scaleItems,       setScaleItems]       = useState<readonly AssessmentScaleItem[]>([]);

  // Unique ratingScaleCode values across all section pages
  const ratingScaleCodes = [...new Set(
    sections.flatMap((s: any) => (s.pages ?? []).map((p: any) => p.ratingScaleCode).filter(Boolean))
  )] as string[];

  const assessmentId = assessmentData?.assessmentId ?? assessmentData?.assessmentDetails?.id;
  const [answers,    setAnswers]    = useState<Record<string, { score: number; title: string }>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isPreviewMode && !previewAssessmentData) return;
    const allIds   = [...orgStatementIds, ...subFuncStatementIds.flat()];
    const clientId = assessmentData?.clientId ?? assessmentData?.assessmentDetails?.clientId ?? '';

    const statementsPromise = allIds.length
      ? Api.getStatementData({ clientId, statementCodeList: allIds.join(',') })
      : Promise.resolve(null);

    const ratingScalePromise = ratingScaleCodes.length
      ? Api.getRatingScaleData({ clientId, RSCodeList: ratingScaleCodes.join(',') })
      : Promise.resolve(null);

    const savedDataPromise = assessmentId && !isPreviewMode
      ? Api.fetchAssessmentQuestionsAnswered(assessmentId)
      : Promise.resolve(null);

    Promise.all([statementsPromise, ratingScalePromise, savedDataPromise])
      .then(([stmtRes, scaleRes, savedRes]: any[]) => {
        // Track actual displayed question counts (only IDs that have statement data)
        let orgQuestionsLen = orgStatementIds.length;
        let subFuncQuestionsLens = subFuncStatementIds.map(arr => arr.length);

        if (stmtRes?.success) {
          const byId: Record<string, QuestionData> = {};
          (stmtRes.response?.data?.data ?? stmtRes.response?.data ?? []).forEach((s: any) => {
            // statementCode matches the statementIds used in sections pages
            const key = s.statementCode ?? s.id ?? s._id;
            byId[key] = { skill: s.name ?? s.title ?? s.skill ?? '', description: s.description ?? '' };
          });
          const orgQs = orgStatementIds.map((id: string) => byId[id]).filter(Boolean);
          const subFuncQs = subFuncStatementIds.map((ids: string[]) => ids.map((id: string) => byId[id]).filter(Boolean));
          setOrgQuestions(orgQs);
          setSubFuncQuestions(subFuncQs);
          // Actual displayed counts (may be less than statementIds if some have no data)
          orgQuestionsLen = orgQs.length;
          subFuncQuestionsLens = subFuncQs.map(arr => arr.length);
        }
        if (scaleRes?.success) {
          // Response shape: [{ "RS4": [ { score, text, description, ... } ] }]
          const rawData = scaleRes.response?.data?.data ?? scaleRes.response?.data;
          const groups: any[] = Array.isArray(rawData) ? rawData : [];
          const opts: any[] = groups.flatMap((g: any) => Object.values(g).flat());
          const mapped: AssessmentScaleItem[] = opts.map((opt: any, idx: number) => ({
            number: opt.score ?? idx + 1,
            title: opt.text ?? '',
            description: opt.description ?? '',
          }));
          if (mapped.length) setScaleItems(mapped);
        }
        if (savedRes?.success) {
          const body = savedRes.response?.data;
          const item = body?.data?.[0] ?? body?.[0] ?? body;
          const scores = item?.results?.scores ?? {};

          // If already completed, redirect so user doesn't re-take it
          if (scores.CompletedDate) {
            navigate('/dashboard', { replace: true });
            return;
          }

          const savedAnswers: any[] = scores.Answers ?? [];
          if (savedAnswers.length) {
            const restored: Record<string, { score: number; title: string }> = {};
            savedAnswers.forEach((a: any) => {
              if (a.StatementID && a.Score) {
                restored[a.StatementID] = { score: Number(a.Score), title: a.StatementTitle ?? '' };
              }
            });
            setAnswers(restored);
          }
          if (scores.StartDate) {
            startDateRef.current = scores.StartDate;
          }

          // Parse MongoDB $numberInt format that the API returns
          const sc = scores.stepsCompleted;
          const rawCompleted = sc?.completed?.$numberInt
            ? Number(sc.completed.$numberInt)
            : Number(sc?.completed ?? 0);
          // Normalize: old format stored question count only (no instruction screens)
          // Take max so old data (e.g. completed=33) is promoted to the correct linear position
          const instrBeforeFirstQ = stepSequence
            .slice(0, stepSequence.indexOf('questionsOrg'))
            .filter(s => !isQuestionStep(s)).length;
          const completedCount = Math.max(
            rawCompleted,
            savedAnswers.length > 0 ? instrBeforeFirstQ + savedAnswers.length : 0
          );

          // Walk using actual rendered question counts, not raw statementIds counts
          if (completedCount > 0) {
            let idx = 0;
            let resumeStepKey = 'done';
            let resumeQIdx: number | undefined = undefined;

            outer: for (const stepKey of stepSequence) {
              if (isQuestionStep(stepKey)) {
                const qLen = stepKey === 'questionsOrg'
                  ? orgQuestionsLen
                  : (subFuncQuestionsLens[parseInt(stepKey.split('_')[1])] ?? 0);
                for (let q = 0; q < qLen; q++) {
                  idx++;
                  if (idx > completedCount) {
                    resumeStepKey = stepKey;
                    resumeQIdx = q;
                    break outer;
                  }
                }
              } else {
                idx++;
                if (idx > completedCount) {
                  resumeStepKey = stepKey;
                  break;
                }
              }
            }

            // Set all fully-completed question groups to their last question (enables correct Back nav)
            const qIndexUpdates: Record<string, number> = {};
            const resumeSeqIdx = stepSequence.indexOf(resumeStepKey);
            for (let i = 0; i < resumeSeqIdx; i++) {
              const s = stepSequence[i];
              if (!isQuestionStep(s)) continue;
              const len = s === 'questionsOrg' ? orgQuestionsLen : (subFuncQuestionsLens[parseInt(s.split('_')[1])] ?? 0);
              if (len > 0) qIndexUpdates[s] = len - 1;
            }
            if (resumeQIdx !== undefined) qIndexUpdates[resumeStepKey] = resumeQIdx;
            if (Object.keys(qIndexUpdates).length > 0) setQIndices(prev => ({ ...prev, ...qIndexUpdates }));
            setStep(resumeStepKey);
          } else {
            // Fallback: no stepsCompleted — infer position from the Answers array
            const answeredIds = new Set(savedAnswers.map((a: any) => a.StatementID).filter(Boolean));
            if (answeredIds.size > 0) {
              const firstUnansweredOrgIdx = orgStatementIds.findIndex(id => !answeredIds.has(id));
              if (firstUnansweredOrgIdx > 0) {
                setStep('questionsOrg');
                setQIndices(prev => ({ ...prev, questionsOrg: firstUnansweredOrgIdx }));
              } else if (firstUnansweredOrgIdx === -1) {
                let resumed = false;
                for (let sfIdx = 0; sfIdx < subFuncStatementIds.length; sfIdx++) {
                  const firstUnanswered = subFuncStatementIds[sfIdx].findIndex(id => !answeredIds.has(id));
                  if (firstUnanswered === -1) continue;
                  if (firstUnanswered > 0) {
                    setStep(`questions_${sfIdx}`);
                    setQIndices(prev => ({ ...prev, [`questions_${sfIdx}`]: firstUnanswered }));
                  } else {
                    setStep(`subIntro_${sfIdx}`);
                  }
                  resumed = true;
                  break;
                }
                if (!resumed) setStep('done');
              }
            }
          }
        }
      })
      .finally(() => setQuestionsLoading(false));
  }, [previewAssessmentData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Dynamic step sequence ──────────────────────────────────────────────

  const stepSequence: string[] = [
    'darkIntro', 'ratingScale', 'ratingExample', 'howToRespond',
    'sectionIntroOrg', 'questionsOrg',
    ...(numSubFunctions > 0 ? ['impactOverview'] : []),
    ...Array.from({ length: numSubFunctions }, (_, i) => [`subIntro_${i}`, `questions_${i}`]).flat(),
    'submission',
    'done',
  ];

  const [step,     setStep]     = useState<string>('darkIntro');
  const [qIndices, setQIndices] = useState<Record<string, number>>({});

  const getQIndex   = (s: string) => qIndices[s] ?? 0;
  const setQIndex   = (s: string, idx: number) => setQIndices(prev => ({ ...prev, [s]: idx }));
  const resetQIndex = (s: string) => setQIndices(prev => ({ ...prev, [s]: 0 }));
  const go = (next: string) => setStep(next);

  const prevStepFor = (current: string): string => {
    const idx = stepSequence.indexOf(current);
    return idx > 0 ? stepSequence[idx - 1] : current;
  };
  const nextStepFor = (current: string): string => {
    const idx = stepSequence.indexOf(current);
    return stepSequence[idx + 1] ?? 'done';
  };

  // ── Progress percent ───────────────────────────────────────────────────

  const isQuestionStep = (s: string) => s === 'questionsOrg' || s.startsWith('questions_');
  // Computed once per render; shared by getPercent, submitProgress, saveAndComplete
  const linearTotal = stepSequence.filter(s => !isQuestionStep(s)).length
    + orgQuestions.length + subFuncQuestions.flat().length;

  const getPercent = (): number => {
    // Use actual displayed question counts so progress aligns with what the user sees
    const linearTotal = stepSequence.filter(s => !isQuestionStep(s)).length
      + orgQuestions.length + subFuncQuestions.flat().length;
    if (linearTotal <= 0) return 0;
    if (step === 'done') return 100;

    const stepIdx = stepSequence.indexOf(step);
    const instrBefore = stepSequence
      .slice(0, stepIdx)
      .filter(s => !isQuestionStep(s)).length;

    // Compute once; used by both question and non-question branches
    const questionsBefore = stepSequence
      .slice(0, stepIdx)
      .reduce((acc, s) => {
        if (!isQuestionStep(s)) return acc;
        if (s === 'questionsOrg') return acc + orgQuestions.length;
        const sfIdx = parseInt(s.split('_')[1]);
        return acc + (subFuncQuestions[sfIdx]?.length || 0);
      }, 0);

    if (isQuestionStep(step)) {
      // Include questions from all preceding question steps (e.g. org questions before functional)
      const linearPos = instrBefore + questionsBefore + getQIndex(step) + 1;
      return Math.round((linearPos / linearTotal) * 100);
    }
    // For non-question steps, also count questions from all preceding question steps
    return Math.round(((instrBefore + questionsBefore) / linearTotal) * 100);
  };

  const percent    = getPercent();
  const isQuestion = isQuestionStep(step);

  const [autoAdvance, setAutoAdvance] = useState(false);
  const [navGlass,    setNavGlass]    = useState(false);
  const shellRef     = useRef<HTMLDivElement>(null);
  const startDateRef = useRef<string | null>(null);

  // ── Layout effects ─────────────────────────────────────────────────────

  useLayoutEffect(() => {
    const shell = shellRef.current;
    if (!shell || typeof window === 'undefined') return;
    const BUDGET = 88;
    const apply = () => {
      const navEl = shell.querySelector('header');
      const navH  = navEl instanceof HTMLElement ? navEl.offsetHeight : 0;
      shell.querySelectorAll('[data-template="question"]').forEach((root) => {
        if (!(root instanceof HTMLElement)) return;
        root.style.setProperty('--compact-t', '0');
        const column = root.querySelector('main')?.firstElementChild;
        const colH   = column instanceof HTMLElement ? column.offsetHeight : 0;
        const t = Math.min(Math.max((navH + colH - window.innerHeight) / BUDGET, 0), 1);
        root.style.setProperty('--compact-t', String(t));
      });
    };
    apply();
    window.addEventListener('resize', apply);
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(apply).catch(() => {});
    }
    return () => window.removeEventListener('resize', apply);
  }, [step, qIndices]);

  useLayoutEffect(() => {
    const sc = shellRef.current;
    if (!sc) return;
    sc.scrollTop = 0;
    setNavGlass(false);
    const raf = requestAnimationFrame(() => { sc.scrollTop = 0; });
    return () => cancelAnimationFrame(raf);
  }, [step, qIndices]);

  useLayoutEffect(() => {
    const sc = shellRef.current;
    if (!sc) return;
    const onScroll = () => setNavGlass(sc.scrollTop > 2);
    onScroll();
    sc.addEventListener('scroll', onScroll, { passive: true });
    return () => sc.removeEventListener('scroll', onScroll);
  }, [questionsLoading]); // re-attach once shellRef is available after loading

  if (!location.state?.assessmentsData && !isPreviewMode) return null;

  const submitProgress = async (
    currentAnswers: Record<string, { score: number; title: string }>,
    completedOn: string | null = null
  ) => {
    if (isPreviewMode) return;
    if (!assessmentId) return;
    if (!startDateRef.current) startDateRef.current = new Date().toISOString();
    // Each instruction screen + each individual question counts as one linear step
    const instrScreensDone = stepSequence
      .slice(0, stepSequence.indexOf(step) + (isQuestionStep(step) ? 0 : 1))
      .filter(s => !isQuestionStep(s)).length;
    const linearCompleted = instrScreensDone + Object.keys(currentAnswers).length;
    const answersPayload = buildAnswersPayload(currentAnswers);
    setSubmitting(true);
    try {
      await Api.updateAssessmentsStatus({
        results: {
          scores: {
            AssessmentID: assessmentId,
            ApplicationID: null,
            AssessmentRef: null,
            AssessmentCategory: 'Impact',
            Answers: answersPayload,
            StartDate: startDateRef.current,
            CompletedDate: completedOn,
            ApplicationStartDate: startDateRef.current,
            ApplicationCompletedDate: completedOn,
            stepsCompleted: { total: linearTotal, completed: linearCompleted },
          },
        },
      }, assessmentId);
    } finally {
      setSubmitting(false);
    }
  };

  const saveAndComplete = async () => {
    if (isPreviewMode) {go('done'); return; }
    if (!assessmentId) return;
    if (!startDateRef.current) startDateRef.current = new Date().toISOString();
    const completedOn = new Date().toISOString();
    const answersPayload = buildAnswersPayload(answers);
    setSubmitting(true);
    try {
      const res = await Api.saveAndCompleteAssessment({
        identifier: 'impact-assessment',
        notificationType: 'AssessmentCompleted',
        assessmentId,
        applicationID: null,
        results: {
          scores: {
            AssessmentID: assessmentId,
            ApplicationID: null,
            AssessmentRef: null,
            AssessmentCategory: 'Impact',
            Answers: answersPayload,
            StartDate: startDateRef.current,
            CompletedDate: completedOn,
            ApplicationStartDate: startDateRef.current,
            ApplicationCompletedDate: completedOn,
            stepsCompleted: { total: linearTotal, completed: linearTotal },
          },
        },
      });
      if (res?.success) go('done');
    } finally {
      setSubmitting(false);
    }
  };

  const stepProps = { brand: BRAND, assessmentName, percent, chrome: false as const, theme };

  // ── Instruction screen renderer ────────────────────────────────────────

  const renderInstruction = (): ReactNode => {
    switch (step) {
      case 'darkIntro':
        return (
          <AssessmentStep
            {...stepProps}
            eyebrow="Introduction"
            title={introContent.heading || assessmentName}
            body={<>{introContent.body}</>}
            actions={<StepActions isLight={isLight} label="Begin" onContinue={async () => { await submitProgress(answers); go('ratingScale'); }} submitting={submitting} />}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 40, width: '100%' }}>
              {infoRowStats.length > 0 && <InfoRow theme={theme} items={infoRowStats} />}
              {introContent.infoText && <SaveProgressNote text={introContent.infoText} />}
            </div>
          </AssessmentStep>
        );

      case 'ratingScale': {
        let bodyParagraphs: string[] = [];
        if (Array.isArray(ratScalePage.body)) {
          bodyParagraphs = ratScalePage.body;
        } else if (ratScalePage.body) {
          bodyParagraphs = [ratScalePage.body];
        }
        return (
          <AssessmentStep
            {...stepProps}
            eyebrow={ratScalePage.sectionLabel || 'Instructions'}
            title={ratScalePage.heading || 'Using the rating scale'}
            body={
              <>
                {(() => {
                  // Use an occurrence counter to generate unique stable keys for duplicate paragraphs
                  const occ = new Map<string, number>();
                  return bodyParagraphs.map((b: string, i: number) => {
                    const count = occ.get(b) ?? 0;
                    occ.set(b, count + 1);
                    const base = hashString(b ?? '');
                    const suffix = count > 0 ? '-' + String(count) : '';
                    const key = base + suffix;
                    // Use the map index for visual spacing (explicit and safe)
                    const margin = i > 0 ? '24px 0 0' : 0;
                    // Content originates from our own API — not user-generated input
                    return <p key={key} style={{ margin }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(b) }} />;
                  });
                })()}
              </>
            }
            actions={<StepActions isLight={isLight} onBack={() => go('darkIntro')} label="Continue" onContinue={async () => { await submitProgress(answers); go('ratingExample'); }} submitting={submitting} />}
          >
            <RatingScaleContent
              isLight={isLight}
              ratingFactors={ratingFactors}
              scalePrompt={unescape(ratScalePage.scalePrompt ?? '')}
              scaleItems={scaleItems as readonly AssessmentExampleItem[]}
            />
          </AssessmentStep>
        );
      }

      case 'ratingExample':
        return (
          <AssessmentStep
            {...stepProps}
            eyebrow={ratExPage.sectionLabel || 'Instructions'}
            title={ratExPage.heading || 'Rating example'}
            body={
              <div style={{ fontFamily: 'var(--hd-font-family-avenir)', fontSize: 19, fontWeight: 350, lineHeight: 'normal' }}>
                {ratExPage.highlight && (
                  <p style={{ margin: 0, fontWeight: 500, color: isLight ? '#00566A' : '#66FCFC', fontFamily: 'inherit' }}>
                    {ratExPage.highlight}
                  </p>
                )}
                {ratExPage.body && (
                  <p style={{ margin: ratExPage.highlight ? '19px 0 0' : 0, color: isLight ? '#334255' : '#EBEDF1', fontFamily: 'inherit' }}>
                    {ratExPage.body}
                  </p>
                )}
              </div>
            }
            actions={<StepActions isLight={isLight} onBack={() => go('ratingScale')} label="Continue" onContinue={async () => { await submitProgress(answers); go('howToRespond'); }} submitting={submitting} />}
          >
            <RatingExampleContent isLight={isLight} exampleQ={exampleQuestion ?? { title: '', description: '', preSelectedValue: 3 }} scaleItems={scaleItems as readonly AssessmentExampleItem[]} />
          </AssessmentStep>
        );

      case 'howToRespond':
        return (
          <AssessmentStep
            {...stepProps}
            eyebrow={howToPage.sectionLabel || 'Instructions'}
            title={howToPage.heading || 'How to respond'}
            actions={<StepActions isLight={isLight} onBack={() => go('ratingExample')} label="Continue" onContinue={async () => { await submitProgress(answers); go('sectionIntroOrg'); }} submitting={submitting} />}
          >
            <HowToRespondSections isLight={isLight} sections={howToSections} />
          </AssessmentStep>
        );

      case 'sectionIntroOrg':
        return (
          <AssessmentStep
            {...stepProps}
            title={orgIntro.heading || 'Organizational Impact'}
            body={<>{orgIntro.body}</>}
            actions={
              <StepActions
                isLight={isLight}
                onBack={() => go('howToRespond')}
                label="Continue"
                onContinue={async () => { await submitProgress(answers); resetQIndex('questionsOrg'); go('questionsOrg'); }}
                submitting={submitting}
              />
            }
          />
        );

      case 'impactOverview':
        return (
          <AssessmentStep
            {...stepProps}
            title={funcIntro.heading || 'Functional Impact'}
            body={<>{funcIntro.body}</>}
            actions={
              <StepActions
                isLight={isLight}
                onBack={() => go('questionsOrg')}
                label="Continue"
                onContinue={async () => { await submitProgress(answers); go('subIntro_0'); }}
                submitting={submitting}
              />
            }
          >
            {funcSubSectionLabels.length > 0 && (
              <AssessmentSectionList items={funcSubSectionLabels.map(label => ({ label }))} theme={theme} />
            )}
          </AssessmentStep>
        );

      case 'submission':
        return (
          <AssessmentStep
            {...stepProps}
            eyebrow={submissionPg.sectionLabel || 'Submission'}
            title={submissionPg.heading || 'Ready to submit?'}
            body={<>{submissionPg.body || 'Please confirm that you want to submit your responses. Once you submit, you will not be able to return to this assessment or change your answers.'}</>}
            actions={
              <StepActions
                isLight={isLight}
                onBack={() => go(prevStepFor(step))}
                label="Continue and submit"
                onContinue={saveAndComplete}
                submitting={submitting}
              />
            }
          />
        );

      case 'done':
        return (
          <AssessmentStep
            {...stepProps}
            preTitleIcon={<CheckIcon isLight={isLight} />}
            title={completionHeading || 'You did it.'}
            body={<>{completionPg.body || 'Thank you for completing the assessment.'}</>}
            actions={<StepActions isLight={isLight} label={isPreviewMode ? "Close" : "Save and close"} onContinue={() => { if (isPreviewMode) { window.close(); navigate(-1); } else { navigate('/dashboard'); } }}  />}
          />
        );

      default:
        // Dynamic sub-function intros: subIntro_0, subIntro_1, …
        if (step.startsWith('subIntro_')) {
          const sfIdx     = parseInt(step.split('_')[1]);
          const sfContent = subFuncIntroSections[sfIdx]?.pages?.[0]?.content ?? {};
          return (
            <AssessmentStep
              {...stepProps}
              template="sub-section"
              eyebrow={sfContent.sectionLabel || 'Functional Impact'}
              title={sfContent.heading || ''}
              body={<>{sfContent.body}</>}
              actions={
                <StepActions
                  isLight={isLight}
                  onBack={() => go(prevStepFor(step))}
                  label="Continue"
                  onContinue={async () => { await submitProgress(answers); resetQIndex(`questions_${sfIdx}`); go(`questions_${sfIdx}`); }}
                  submitting={submitting}
                />
              }
            />
          );
        }
        return null;
    }
  };

  // ── Question set renderer ──────────────────────────────────────────────

  const renderQuestions = (questions: QuestionData[], stmtIds: string[], category: string, qStep: string): ReactNode => {
    if (questionsLoading) return <Loader />;

    const qIdx = getQIndex(qStep);
    const prev = prevStepFor(qStep);
    const next = nextStepFor(qStep);

    return (
      <>
        <StepTransition
          index={qIdx}
          durationMs={1250}
          style={{ flex: '1 1 auto', minHeight: 'auto', gridTemplateRows: 'minmax(min-content, 1fr)' }}
        >
          {({ index: i, phase, direction }: any) => {
            const q = questions[i] ?? { skill: '', description: '' };
            return (
              <AssessmentQuestion
                theme={theme}
                chrome={false}
                phase={phase}
                direction={direction}
                brand={BRAND}
                assessmentName={assessmentName}
                percent={percent}
                category={category}
                questionNumber={i + 1}
                totalQuestions={questions.length}
                title={q.skill}
                section={q.section}
                autoAdvance={autoAdvance}
                onAutoAdvanceChange={setAutoAdvance}
                prompt={q.description}
                items={scaleItems}
                initialSelected={answers[stmtIds[i]]?.score as 1 | 2 | 3 | 4 | 5 | undefined}
                errorMessage="Please select a response to continue."
                submitting={submitting}
                onBack={phase !== 'exit' ? () => (i > 0 ? setQIndex(qStep, i - 1) : go(prev)) : undefined}
                onAnswered={phase !== 'exit' ? async (rank) => {
                  const sid = stmtIds[i];
                  const updated = { ...answers, ...(sid ? { [sid]: { score: rank, title: q.skill } } : {}) };
                  setAnswers(updated);
                  await submitProgress(updated);
                  if (i < questions.length - 1) setQIndex(qStep, i + 1); else go(next);
                } : undefined}
              />
            );
          }}
        </StepTransition>
      </>
    );
  };

  // ── Content dispatcher ─────────────────────────────────────────────────

  const renderContent = (): ReactNode => {
    if (step === 'questionsOrg') {
      const orgQSec      = findSection(sections, 'organizational-impact-questions');
      const sectionLabel = orgQSec?.pages?.[0]?.content?.sectionLabel || 'Organizational Impact';
      return renderQuestions(orgQuestions, orgStatementIds, sectionLabel, 'questionsOrg');
    }

    if (step.startsWith('questions_')) {
      const sfIdx     = parseInt(step.split('_')[1]);
      const sfContent = subFuncIntroSections[sfIdx]?.pages?.[0]?.content ?? {};
      const category  = sfContent.heading || `Sub-function ${sfIdx + 1}`;
      return renderQuestions(subFuncQuestions[sfIdx] ?? [], subFuncStatementIds[sfIdx] ?? [], category, step);
    }

    return (
      <div
        key={step}
        style={{
          flex: '1 1 auto',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          gridTemplateRows: 'minmax(min-content, 1fr)',
        }}
      >
        {renderInstruction()}
      </div>
    );
  };

  // ── Shell ──────────────────────────────────────────────────────────────

  if (questionsLoading) {
    return (
      <ThemeProvider theme={heidrickTheme}>
        <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isLight ? 'var(--hd-color-assessment-bg-light)' : 'var(--hd-color-assessment-bg)' }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', border: `3px solid ${isLight ? 'rgba(0,86,106,0.15)' : 'rgba(102,252,252,0.15)'}`, borderTopColor: isLight ? '#00566A' : '#66FCFC', animation: 'hd-spin 0.9s linear infinite' }} />
          <style>{`@keyframes hd-spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={heidrickTheme}>
      <div style={{
        position: 'fixed',
        inset: 0,
        background: isLight ? 'var(--hd-color-assessment-bg-light)' : 'var(--hd-color-assessment-bg)',
        color: isLight ? 'var(--hd-text-hero-h1-color-light)' : 'var(--hd-text-hero-h1-color)',
        isolation: 'isolate',
        overflow: 'hidden',
      }}>
        {isQuestion
          ? <AssessmentQuestionBackground theme={theme} />
          : <AssessmentBackground theme={theme} />
        }

        <div
          ref={shellRef}
          style={{ position: 'absolute', inset: 0, zIndex: 1, overflowY: 'auto', overflowAnchor: 'none' }}
        >
          <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'sticky', top: 0, zIndex: 3 }}>
              <AssessmentNav
                assessmentName={assessmentName}
                percent={percent}
                scrolled={navGlass}
                theme={theme}
                brand={`hs`} 
                headerLogo={sanitizeImageUrl(headerLogo)}
              />
            </div>

            {renderContent()}

            <div style={{ marginTop: 40 }}>
              <Footer
                variant={isLight ? 'assessment-light' : 'assessment'}
                links={computedFooterLinks ?? footerLinks ?? footerValues().map(f => ({ label: f.url, onClick: f.onClick }))}
              />
            </div>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
}
